import { useState, useCallback } from 'react';
import { useAppSelector } from '../../../../../../app/store';
import type { InsuranceType } from '../../../../../../services/EncounterService/types/encounterTypes';
import type { PayerType } from '../../../../../../services/ConfigurationService/types';

export interface InsuranceAddFormData {
  priority: number;
  payerCode: string;
  payerName: string;
  payerId: string;
  policyNumber: string;
  groupNumber: string;
  groupName: string;
  insuredsFirstName: string;
  insuredsMiddleName: string;
  insuredsLastName: string;
  insuredsSuffix: string;
  insuredsStreet1: string;
  insuredsStreet2: string;
  insuredsCity: string;
  insuredsState: string;
  insuredsZip: string;
  insuredsRelationship: string;
  insuredsDateOfBirth: string | undefined;
  authStartDate: string | undefined;
  authEndDate: string | undefined;
}

const createInitialFormData = (nextPriority: number): InsuranceAddFormData => ({
  priority: nextPriority,
  payerCode: '',
  payerName: '',
  payerId: '',
  policyNumber: '',
  groupNumber: '',
  groupName: '',
  insuredsFirstName: '',
  insuredsMiddleName: '',
  insuredsLastName: '',
  insuredsSuffix: '',
  insuredsStreet1: '',
  insuredsStreet2: '',
  insuredsCity: '',
  insuredsState: '',
  insuredsZip: '',
  insuredsRelationship: '09',
  insuredsDateOfBirth: undefined,
  authStartDate: undefined,
  authEndDate: undefined,
});

export const useInsuranceAddForm = () => {
  const encounter = useAppSelector(state => state.encounter.currentEncounter);
  
  // Calculate next priority based on existing insurances
  const nextPriority = (encounter?.insurances?.length ?? 0) + 1;
  
  const [formData, setFormData] = useState<InsuranceAddFormData>(createInitialFormData(nextPriority));
  const [selectedPayer, setSelectedPayer] = useState<PayerType | null>(null);
  const [sameAsPatient, setSameAsPatient] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = useCallback((field: keyof InsuranceAddFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handlePayerChange = useCallback((payer: PayerType | null) => {
    setSelectedPayer(payer);
    if (payer) {
      setFormData(prev => ({
        ...prev,
        payerCode: payer.payerCode,
        payerName: payer.payerName,
        payerId: payer.id,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        payerCode: '',
        payerName: '',
        payerId: '',
      }));
    }
  }, []);

  const handleSameAsPatientChange = useCallback((checked: boolean) => {
    setSameAsPatient(checked);
    if (checked && encounter?.patient) {
      const patient = encounter.patient;
      setFormData(prev => ({
        ...prev,
        insuredsFirstName: patient.firstName || '',
        insuredsMiddleName: patient.middleName || '',
        insuredsLastName: patient.lastName || '',
        insuredsSuffix: patient.suffix || '',
        insuredsStreet1: patient.street1 || '',
        insuredsStreet2: patient.street2 || '',
        insuredsCity: patient.city || '',
        insuredsState: patient.state || '',
        insuredsZip: patient.zip || '',
        insuredsRelationship: '01', // Self
        insuredsDateOfBirth: patient.birthDate || '',
      }));
    }
  }, [encounter?.patient]);

  const validateForm = useCallback((): boolean => {
    if (!formData.payerName?.trim()) {
      setError('Payer Name is required');
      return false;
    }

    if (!formData.policyNumber?.trim()) {
      setError('Policy Number is required');
      return false;
    }

    return true;
  }, [formData.payerName, formData.policyNumber]);

  const createInsuranceData = useCallback((): InsuranceType => {
    // Clean up empty string date fields before sending to backend
    const cleanedFormData = { ...formData };
    if (cleanedFormData.authStartDate === '') cleanedFormData.authStartDate = undefined;
    if (cleanedFormData.authEndDate === '') cleanedFormData.authEndDate = undefined;
    if (cleanedFormData.insuredsDateOfBirth === '') cleanedFormData.insuredsDateOfBirth = undefined;

    return {
      ...cleanedFormData,
      id: `insurance_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      sameAsPatient,
    } as InsuranceType;
  }, [formData, sameAsPatient]);

  const resetForm = useCallback(() => {
    setFormData(createInitialFormData(nextPriority));
    setSelectedPayer(null);
    setSameAsPatient(false);
    setError('');
  }, [nextPriority]);

  return {
    formData,
    selectedPayer,
    sameAsPatient,
    error,
    setError,
    handleInputChange,
    handlePayerChange,
    handleSameAsPatientChange,
    validateForm,
    createInsuranceData,
    resetForm,
  };
};
